import type { BreakContribution, HoursRateContribution, OvertimeContribution } from "../../../utils/models";
import type { WageSettingsTarget } from "../../team/WageSettings/deepLink";
import type { CompositionLine } from "../wageComposition";

/**
 * DAT-906 · One thing a wage breakdown's hover card shows the configuration row of: a rule the line
 * captured (as it stood when the shift was priced), or the shift's agency.
 */
export type RuleSubject =
	| { kind: "hours_rate"; contribution: HoursRateContribution }
	| { kind: "overtime"; contribution: OvertimeContribution }
	| { kind: "break"; contribution: BreakContribution }
	/**
	 * A legacy row (stored before rules were captured) names only its rate type: the rules today that
	 * match it — an hours-rate rule by name, overtime by kind, a break by the department's break rules.
	 */
	| { kind: "legacy"; target: LegacyRuleTarget; rateType: string };

/** The Wage Settings tables a rate type can point at (`rateTypeTarget`): never a training card. */
export type LegacyRuleTarget = Exclude<WageSettingsTarget, { section: "training-orientation" }>;

export const isLegacyRuleTarget = (target: WageSettingsTarget): target is LegacyRuleTarget => target.section !== "training-orientation";

export type RuleCardSubject = RuleSubject | { kind: "agency" };

/**
 * Everything behind a paid segment: each hours-rate rule it applied, its overtime rule, and — the base of
 * an agency shift being the agency's bill rate — the agency's row, when the viewer may read it.
 */
export const segmentCardSubjects = (lines: readonly CompositionLine[], includeAgency: boolean): RuleCardSubject[] => [
	...lines.flatMap((line): RuleSubject[] => {
		if (line.type === "hours_rate") return [{ kind: "hours_rate", contribution: line.contribution }];
		if (line.type === "overtime") return [{ kind: "overtime", contribution: line.contribution }];
		return [];
	}),
	...(includeAgency ? [{ kind: "agency" } as const] : []),
];

/**
 * The department whose Wage Settings hold the rule: an overtime rule can be the home department's (a
 * float-pool nurse's OT follows it). DEFAULT: a line with no captured department — and a legacy row, which
 * captured nothing — is looked up in the department on screen, the only one it can be known to belong to.
 */
export const subjectDepartmentId = (subject: RuleSubject, viewedDepartmentId: string): string =>
	subject.kind === "overtime" && subject.contribution.departmentId !== null ? subject.contribution.departmentId : viewedDepartmentId;
